<template>
    <div class="bg-white rounded-5">
        <ProgressBar :current_step="current_step" />

        <div v-if="initialDataFetched" class="px-5 mx-4">
            <ApplicationDetails
                v-if="current_step === 1"
                :applicationId="applicationId"
                :applicationData="applicationData"
                :companyList="companyList"
                :dashboardList="dashboardList"
                :errors="applicationErrors"
                @captureValues="captureValues"
                @handlePageChangeDisabled="handlePageChangeDisabled"
            />
            <ApplicationConfiguration
                v-else-if="current_step === 2"
                :applicationId="applicationId"
                :applicationData="applicationData"
                :introSlides="introSlideList"
                :errors="applicationErrors"
                @captureValues="captureValues"
                @handlePageChangeDisabled="handlePageChangeDisabled"
            />
            <CustomisedThemeDesign
                v-else-if="current_step === 3"
                :applicationData="applicationData"
                :errors="applicationErrors"
                @captureValues="captureValues"
            />
        </div>

        <div class="mx-5 py-5 d-flex justify-content-end">
            <button
                class="other-btn cancel-btn"
                :class="pageChangeDisabled ? 'btn-disabled' : ''"
                @click="$router.back()"
                :disabled="pageChangeDisabled"
            >
                Cancel
            </button>
            <button
                v-if="current_step > 1"
                class="other-btn"
                :class="pageChangeDisabled ? 'btn-disabled' : ''"
                @click="onClickBack"
                :disabled="pageChangeDisabled"
            >
                Back to Previous
            </button>
            <button
                v-if="current_step < max_step"
                class="save-btn"
                :class="pageChangeDisabled ? 'btn-disabled' : ''"
                :disabled="pageChangeDisabled"
                @click="onClickNext"
            >
                Next
            </button>
            <button v-if="current_step == max_step" class="save-btn" @click="saveApplication">Save</button>
        </div>

        <Loading v-if="loading" />
    </div>
</template>

<script>
    import ProgressBar from './progress_bar.vue';
    import Loading from '@/components/general/loading/loading.vue';
    import ApplicationDetails from './application_details.vue';
    import ApplicationConfiguration from './application_configuration.vue';
    import CustomisedThemeDesign from './customised_theme_design.vue';
    import { companies, dashboardBuilder, applicationBuilder, slides } from '@/util/apiRequests';

    const initialAppColors = {
        primary_color: '#F1f7ff',
        primary_text_color: '#000000',
        secondary_color: '#1773b1',
        secondary_text_color: '#ffffff',
    };

    const initialApplicationData = {
        application_type: 'mobex-health-home',
        application_name: null,
        android_ios_id: null,
        company_belongs: null,
        dashboard_belongs: null,
        app_logo: null,
        app_startup_logo: null,
        app_startup_screen_background: null,
        signup_logo: null,
        signup_banner_image: null,
        signup_banner_image_title: null,
        signup_banner_image_content: null,
        signup_enabled: 1,
        registration_approval_enabled: 0,
        registration_id: null,
        medicaid_state: null,
        color_configuration: { ...initialAppColors },
        project_tags: [],
    };

    const initialApplicationErrors = {
        application_name: null,
        android_ios_id: null,
        company_belongs: null,
        dashboard_belongs: null,
        app_logo: null,
        app_startup_logo: null,
        app_startup_screen_background: null,
        signup_logo: null,
        signup_banner_image: null,
        signup_banner_image_content: null,
        introSlideList: null,
    };

    export default {
        name: 'ApplicationBuilder',
        components: {
            ProgressBar,
            ApplicationDetails,
            ApplicationConfiguration,
            CustomisedThemeDesign,
            Loading,
        },
        data() {
            return {
                current_step: 1,
                max_step: 3,
                companyList: [],
                dashboardList: [],
                applicationData: { ...initialApplicationData },
                applicationErrors: { ...initialApplicationErrors },
                introSlideList: [],
                deletedSlideIds: [],
                applicationId: null,
                loading: true,
                initialDataFetched: false,
                isNextButtonProcessing: false,
                pageChangeDisabled: false,
            };
        },
        methods: {
            async getApplicationData() {
                try {
                    const endpoint = applicationBuilder.getApplication(this.applicationId);
                    const response = await this.$api.get(endpoint);

                    if (!response.data.success) {
                        this.$router.back();
                        throw response.data.error;
                    }
                    const rawData = response.data.data;

                    this.applicationData = {
                        ...rawData,
                        color_configuration: JSON.parse(rawData.color_configuration),
                        registration_approval_enabled:
                            rawData.registration_approval_enabled !== undefined
                                ? rawData.registration_approval_enabled
                                : initialApplicationData.registration_approval_enabled,
                        medicaid_state: rawData.medicaid_state || null,
                    };
                } catch (error) {
                    console.error('Failed to fetch application data:', error);
                    this.$toasted.error('Failed to fetch application data');
                }
            },
            async getLastApplicationId() {
                try {
                    const endpoint1 = applicationBuilder.getLastApplicationId();
                    const response = await this.$api.get(endpoint1);
                    if (!response.data.success) {
                        throw response.data.error;
                    }
                    this.applicationId = response.data.response.AUTO_INCREMENT;
                } catch (err) {
                    console.error(err);
                }
            },
            async getAllCompanies() {
                try {
                    const endpoint = companies.getAllCompanies();
                    const response = await this.$api.get(endpoint);
                    this.companyList = response.data.info;

                    // Only default to current user's company for NEW applications
                    if (!this.applicationData.company_belongs && this.$store.state?.user?.company_id) {
                        this.applicationData.company_belongs = this.$store.state.user.company_id;
                    }
                } catch (err) {
                    this.$toasted.error('Failed to fetch Companies');
                }
            },
            async getAllDashboards() {
                try {
                    let response = await this.$api.get(
                        dashboardBuilder.getAllTemplateList('completed', 'mobex-health-plus')
                    );

                    this.dashboardList = response.data.templateMetaList;
                } catch (err) {
                    this.$toasted.error('Failed to fetch Dashboards');
                }
            },
            async captureValues(event) {
                const { applicationData } = event;

                if (!applicationData) return;

                const {
                    application_name,
                    android_ios_id,
                    company_belongs,
                    dashboard_belongs,
                    app_logo,
                    app_startup_logo,
                    app_startup_screen_background,
                    signup_logo,
                    signup_banner_image,
                    signup_banner_image_title,
                    signup_banner_image_content,
                    introSlideList,
                    deletedSlideIds,
                    color_configuration,
                    waiting_animation,
                    project_tags,
                    signup_enabled,
                    registration_approval_enabled,
                    registration_id,
                    medicaid_state,
                } = applicationData;

                if (application_name !== undefined) {
                    this.applicationData.application_name = application_name.trim();
                }
                if (android_ios_id !== undefined) {
                    this.applicationData.android_ios_id = android_ios_id.trim();
                }
                if (company_belongs !== undefined) {
                    this.applicationData.company_belongs = company_belongs;
                }
                if (dashboard_belongs !== undefined) {
                    this.applicationData.dashboard_belongs = dashboard_belongs;
                }
                if (app_logo !== undefined) {
                    this.applicationData.app_logo = app_logo;
                }
                if (app_startup_logo !== undefined) {
                    this.applicationData.app_startup_logo = app_startup_logo;
                }
                if (app_startup_screen_background !== undefined) {
                    this.applicationData.app_startup_screen_background = app_startup_screen_background;
                }
                if (signup_logo !== undefined) {
                    this.applicationData.signup_logo = signup_logo;
                }
                if (signup_banner_image !== undefined) {
                    this.applicationData.signup_banner_image = signup_banner_image;
                }
                if (signup_banner_image_title !== undefined) {
                    this.applicationData.signup_banner_image_title = signup_banner_image_title;
                }
                if (signup_banner_image_content !== undefined) {
                    this.applicationData.signup_banner_image_content = signup_banner_image_content;
                }
                if (introSlideList !== undefined) {
                    this.introSlideList = introSlideList;
                }
                if (deletedSlideIds !== undefined) {
                    this.deletedSlideIds = deletedSlideIds;
                }
                if (color_configuration !== undefined) {
                    this.applicationData.color_configuration = color_configuration;
                }
                if (waiting_animation !== undefined) {
                    this.applicationData.waiting_animation = waiting_animation;
                }
                if (project_tags !== undefined) {
                    this.applicationData.project_tags = project_tags;
                }
                if (signup_enabled !== undefined) {
                    this.applicationData.signup_enabled = signup_enabled;
                }
                if (registration_approval_enabled !== undefined) {
                    this.applicationData.registration_approval_enabled = registration_approval_enabled;
                }
                if (registration_id !== undefined) {
                    this.applicationData.registration_id = registration_id;
                }
                if (medicaid_state !== undefined) {
                    this.applicationData.medicaid_state = medicaid_state;
                }
            },
            async onClickNext() {
                try {
                    if (this.isNextButtonProcessing) {
                        return;
                    }

                    this.isNextButtonProcessing = true;

                    let err = 0;

                    const {
                        application_name,
                        android_ios_id,
                        company_belongs,
                        dashboard_belongs,
                        app_logo,
                        app_startup_logo,
                        app_startup_screen_background,
                        signup_logo,
                        signup_banner_image,
                        signup_banner_image_title,
                        signup_banner_image_content,
                        waiting_animation,
                    } = this.applicationData;

                    if (this.current_step === 1) {
                        if (!application_name) {
                            err++;
                            this.applicationErrors.application_name = 'Please enter application Name';
                        }
                        if (!android_ios_id) {
                            err++;
                            this.applicationErrors.android_ios_id = 'Please enter app store ID';
                        }
                        if (!company_belongs) {
                            err++;
                            this.applicationErrors.company_belongs = 'Please select a company';
                        }
                        if (!dashboard_belongs) {
                            err++;
                            this.applicationErrors.dashboard_belongs = 'Please select a dashboard';
                        }
                        if (!app_logo) {
                            err++;
                            this.applicationErrors.app_logo = 'Please upload app logo';
                        }
                        if (!err) {
                            const data = {
                                application_name,
                                android_ios_id,
                                dashboard_id: this.applicationId,
                            };

                            if (!this.$route.query.id) {
                                delete data.dashboard_id;
                            }

                            const endpoint = applicationBuilder.checkUniqueApplicationExist();
                            const res = await this.$api.post(endpoint, data);

                            if (!res.data.success) {
                                throw res.data.error;
                            }

                            if (res.data.data.application_name) {
                                err++;
                                this.applicationErrors.application_name = 'Application name already exist';
                            }
                            if (res.data.data.android_ios_id) {
                                err++;
                                this.applicationErrors.android_ios_id = 'App store id already exist';
                            }
                        }
                    } else if (this.current_step === 2) {
                        if (!app_startup_logo) {
                            err++;
                            this.applicationErrors.app_startup_logo = 'Please upload startup logo';
                        }
                        if (!app_startup_screen_background) {
                            err++;
                            this.applicationErrors.app_startup_screen_background =
                                'Please upload startup screen background';
                        }
                        if (!signup_logo) {
                            err++;
                            this.applicationErrors.signup_logo = 'Please upload signup logo';
                        }
                        if (!waiting_animation) {
                            err++;
                            this.applicationErrors.waiting_animation = 'Please upload waiting animation';
                        }
                        if (!signup_banner_image || !signup_banner_image_title || !signup_banner_image_content) {
                            err++;
                            this.applicationErrors.signup_banner_image_content =
                                'Please add signup banner image and content';
                        }
                        if (this.introSlideList.length < 3) {
                            err++;
                            this.applicationErrors.introSlideList = 'Please add atleast three product intro slide';
                        }
                        const activeIntroSlides = this.introSlideList.filter((slide) => slide.is_active);
                        if (!this.applicationErrors.introSlideList && activeIntroSlides.length < 3) {
                            err++;
                            this.applicationErrors.introSlideList = 'Three product intro slide should be active';
                        }
                    }

                    if (err) return;

                    this.applicationErrors = { ...initialApplicationErrors };

                    // Move to the next step
                    if (this.current_step < this.max_step) {
                        this.current_step++;
                    }
                } catch (error) {
                    console.error('Error in onClickNext:', error);
                } finally {
                    this.isNextButtonProcessing = false; // Unlock the function
                }
            },
            handlePageChangeDisabled(val) {
                this.pageChangeDisabled = val;
            },
            onClickBack() {
                this.applicationErrors = { ...initialApplicationErrors };
                this.current_step > 1 ? this.current_step-- : this.current_step;
            },
            async getSlides() {
                try {
                    const endpoint = slides.getSlide(this.applicationId);
                    const res = await this.$api.get(endpoint);
                    if (!res.data.success) {
                        throw res.data.error;
                    }
                    this.introSlideList = res.data.message;
                } catch (err) {
                    console.error('Failed to fetch intro slides', err);
                    this.$toasted.error('Failed to fetch intro slides');
                }
            },
            async handleSlides() {
                try {
                    const endpoint = slides.createSlides(this.applicationId);
                    const res = await this.$api.post(endpoint, { slides: this.introSlideList });
                    if (!res.data.success) {
                        throw res.data.error;
                    }
                } catch (err) {
                    console.error('Failed to create intro slides', err);
                    this.$toasted.error('Failed to create intro slides');
                    throw err;
                }
            },
            async deleteSlides() {
                try {
                    const endpoint = slides.deleteSlides();
                    const res = await this.$api.post(endpoint, { ids: this.deletedSlideIds });
                    if (!res.data.success) {
                        throw res.data.error;
                    }
                } catch (err) {
                    console.error('Failed to delete intro slides', err);
                    throw err;
                }
            },
            async saveApplication() {
                try {
                    this.loading = true;
                    let res;

                    const {
                        application_type,
                        application_name,
                        android_ios_id,
                        company_belongs,
                        dashboard_belongs,
                        app_logo,
                        app_startup_logo,
                        app_startup_screen_background,
                        signup_logo,
                        signup_banner_image,
                        signup_banner_image_title,
                        signup_banner_image_content,
                        color_configuration,
                        waiting_animation,
                        project_tags,
                        signup_enabled,
                        registration_approval_enabled,
                        registration_id,
                        medicaid_state,
                    } = this.applicationData;

                    const data = {
                        application_type,
                        application_name,
                        android_ios_id,
                        company_belongs,
                        dashboard_belongs,
                        app_logo,
                        app_startup_logo,
                        app_startup_screen_background,
                        signup_logo,
                        signup_banner_image,
                        signup_banner_image_title,
                        signup_banner_image_content,
                        color_configuration,
                        waiting_animation,
                        project_tags,
                        signup_enabled,
                        registration_approval_enabled,
                        registration_id,
                        medicaid_state,
                    };

                    if (!this.$route.query.id) {
                        const endpoint = applicationBuilder.createApplication();
                        res = await this.$api.post(endpoint, data);
                    } else {
                        const endpoint = applicationBuilder.updateApplication(this.applicationId);
                        res = await this.$api.put(endpoint, data);
                    }

                    if (!res.data.success) {
                        throw res.data.error;
                    }

                    if (!this.$route.query.id) {
                        this.applicationId = res.data.data.insertId;
                    }

                    if (this.introSlideList.length) {
                        this.introSlideList =
                            this.introSlideList
                                ?.map((el) => {
                                    const { id, isCreated, isUpdated, description, file_id, is_active } = el;
                                    if (isCreated || isUpdated) {
                                        return {
                                            id: isUpdated ? id : undefined,
                                            description,
                                            file_id,
                                            is_active,
                                        };
                                    }
                                })
                                .filter(Boolean) ?? [];
                        await this.handleSlides();
                    }

                    if (this.deletedSlideIds.length) {
                        await this.deleteSlides();
                    }

                    this.$toasted.success('Application saved successfully');
                    this.$router.push({ name: 'ApplicationBuilderList' });
                } catch (error) {
                    this.$toasted.error(error);
                } finally {
                    this.loading = false;
                }
            },
        },
        async created() {
            if (this.$route.query.id) {
                this.applicationId = Number(this.$route.query.id);
                await this.getApplicationData();
                await this.getSlides();
            } else {
                await this.getLastApplicationId();
            }

            await this.getAllCompanies();
            await this.getAllDashboards();

            this.initialDataFetched = true;
            this.loading = false;
        },
    };
</script>

<style scoped>
    .other-btn {
        background: #fff;
        box-shadow: 0px 8px 22px 6px rgba(0, 0, 0, 0.24), inset 0px 4px 4px rgba(220, 220, 220, 0.25);
        color: #5057c3;
        width: 200px;
        height: 50px;
        font-family: Roboto;
        font-size: 16px;
        border-radius: 5px;
        border: 1px solid 5057C3;
        font-weight: 500;
    }

    .cancel-btn {
        color: #fff;
        background: var(--button-color);
        width: 150px;
    }

    .save-btn {
        background: #5057c3;
        box-shadow: 0px 8px 22px 6px rgba(0, 0, 0, 0.24), inset 0px 4px 4px rgba(220, 220, 220, 0.25);
        color: white;
        width: 150px;
        height: 50px;
        font-family: Roboto;
        font-size: 16px;
        border-radius: 5px;
    }

    .btn-disabled {
        cursor: not-allowed;
        opacity: 0.8;
    }
</style>
